import { isNil, toNumber, isArray } from 'lodash'
import axios from 'axios'
import { appUrls } from '../config/ajax'
import Qs from 'qs'

interface IndexedPosts {
  [key: string]: HTMLElement
}

class PostVoting {
  posts: IndexedPosts
  voteButtons: Element[]

  constructor() {
    this.posts = {}
    this.voteButtons = []
  }

  public init = () => {
    this.getPosts().then(this.verifyPostIds).then(this.updatePosts)
    this.registerButtonListeners()
  }

  /**************************
   * init methods
   **************************/

  private registerButtonListeners = (): void => {
    this.voteButtons = Array.from(document.querySelectorAll('.prp-post-vote'))
    this.voteButtons.map((button) => {
      button.addEventListener('click', (e) => {
        const userVote = button.getAttribute('data-user-vote')
        const postId = (e.target as HTMLButtonElement).closest('[data-post-id]')?.getAttribute('data-post-id')

        // begin processing if ready
        if (!isNil(userVote) && !isNil(postId)) {
          this.processVote(toNumber(postId), userVote)
        }
      })
    })
  }

  private getPosts = (): Promise<string[]> =>
    new Promise((resolve) => {
      const posts = Array.from(document.querySelectorAll('[data-post-id]'))
      const postIds: string[] = []

      // loop through and store posts in obj
      for (const el of posts) {
        const post = <HTMLElement>el
        const id = post.getAttribute('data-post-id')

        // set post and postIds
        if (!isNil(id)) {
          this.posts[id] = post
          postIds.push(id)
        }
      }

      // return post ids for verification
      resolve(postIds)
    })

  /**************************
   * actions
   **************************/

  private updatePostStatus = (postId: number, status: string): void => {
    this.posts[postId].querySelector('.prp-vote-status')!.textContent = status
  }

  private disableVoting = (postId: number, isDisabled = true): void => {
    Array.from(this.posts[postId].querySelectorAll('.prp-post-vote')).map((button) => {
      // set ARIA
      button.setAttribute('aria-disabled', String(isDisabled))

      // disable button
      if (isDisabled) {
        button.setAttribute('disabled', String(isDisabled))
        return
      }

      // enable button
      button.removeAttribute('disabled')
    })
  }

  private updatePosts = (verifiedPosts: PostVotingData[] | unknown): void => {
    if (!isArray(verifiedPosts)) return
    verifiedPosts.forEach((verifiedPost) => {
      this.updatePost(verifiedPost)
    })
  }

  private updatePost = (post: PostVotingData): void => {
    // update counts
    this.posts[post.postId].querySelector('.prp-upvote-total')!.textContent = String(post.upVotes)
    this.posts[post.postId].querySelector('.prp-downvote-total')!.textContent = String(post.downVotes)

    // can't vote, disable buttons
    if (!post.canVote) {
      this.disableVoting(post.postId, true)
      this.updatePostStatus(post.postId, 'VOTED')
      return
    }

    this.disableVoting(post.postId, false)
    this.updatePostStatus(post.postId, 'VOTE')
  }

  /**************************
   * requests
   **************************/

  private verifyPostIds = async (postIds: string[]): Promise<PostVotingData[]> => {
    // bail early if nothing to verify
    if (!postIds || postIds.length === 0) {
      return []
    }

    // axios sends using application/x-www-form-urlecnoded format
    const data = {
      action: 'get_post_votes',
      nonce: WP_AJAX.wpRestNonce,
      postIds,
    }

    const result = await axios.post<PostVotingData[] | { success: boolean; data?: { message?: string } }>(
      String(appUrls.ajax),
      Qs.stringify(data)
    )

    // WordPress error shape: { success: false, data: { message } }
    if (result && typeof result.data === 'object' && !isArray(result.data)) {
      const payload: any = result.data
      if (payload?.success === false) {
        console.warn('get_post_votes error:', payload?.data?.message || 'unknown')
        return []
      }
    }

    return isArray(result.data) ? result.data : []
  }

  private processVote = (postId: number, userVote: string): void => {
    const userVoteNumber = userVote === 'up' ? 1 : -1

    this.disableVoting(postId, true)
    this.updatePostStatus(postId, 'VOTING')

    const data = {
      action: 'process_vote',
      nonce: WP_AJAX.wpRestNonce,
      userVote: userVoteNumber,
      postId: postId,
    }

    // send request
    axios
      .post<PostVotingData | { success: boolean; data?: { message?: string } }>(String(appUrls.ajax), Qs.stringify(data))
      .then((response) => {
        const payload: any = response.data
        if (payload && typeof payload === 'object' && payload.success === false) {
          console.warn('process_vote error:', payload?.data?.message || 'unknown')
          this.updatePostStatus(postId, 'ERROR')
          this.disableVoting(postId, false)
          return
        }
        this.updatePost(payload as PostVotingData)
      })
      .catch((error) => {
        console.log('error', error)
        this.updatePostStatus(postId, 'ERROR')
        this.disableVoting(postId, false)
      })
  }
}

export default PostVoting
